import { $insertGeneratedNodes } from '@lexical/clipboard';
import { $generateNodesFromDOM } from '@lexical/html';
import type { BaseSelection, LexicalEditor } from 'lexical';
import { $getRoot, $getSelection, $isElementNode } from 'lexical';

import { cleanImportedHtml } from '../features/rich-text/registerImportSanitizer';
import type { EditorMode } from '../types';

const parser = new DOMParser();

/** The caret Lexical kept from when the editor was last active, or the end when there is none (never focused, or after a quiet load). */
const $caret = (): BaseSelection => $getSelection() ?? $getRoot().selectEnd();

/** Inserts text at the caret (replacing what is selected), picking up the formatting there; `\n` becomes a line break. */
export const $insertTextAtCaret = (text: string) => $caret().insertRawText(text);

/**
 * Inserts HTML at the caret, sanitised like a paste (`cleanImportedHtml`).
 * - Rich: imported as nodes, the way a paste is.
 * - Plain: only the text, with each block on its own line.
 */
export const $insertHtmlAtCaret = (editor: LexicalEditor, html: string, mode: EditorMode) => {
  const nodes = $generateNodesFromDOM(editor, parser.parseFromString(cleanImportedHtml(html), 'text/html'));
  if (mode === 'rich') {
    $insertGeneratedNodes(editor, nodes, $caret());
    return;
  }

  // Inline nodes (text, links) run on along a line; a block starts a new one
  const lines: string[] = [];
  let line: string | null = null;
  for (const node of nodes) {
    if ($isElementNode(node) && !node.isInline()) {
      if (line !== null) lines.push(line);
      lines.push(node.getTextContent());
      line = null;
    } else {
      line = (line ?? '') + node.getTextContent();
    }
  }
  if (line !== null) lines.push(line);
  $insertTextAtCaret(lines.join('\n'));
};
